import { useRef, useState } from "react";
import type {
  OnOpenChangeData,
  OpenPopoverEvents,
} from "@fluentui/react-components";
import {
  mergeClasses,
  Popover,
  PopoverSurface,
  PopoverTrigger,
} from "@fluentui/react-components";
import { Dismiss12Regular } from "@fluentui/react-icons";
import { useStyles } from "./PinnablePopover.styles";
import type { IPinnablePopoverProps } from "./PinnablePopover.types";

export const PinnablePopover = ({
  surfaceLabel,
  trigger,
  children,
  showCloseButton = true,
  surfaceClassName,
}: IPinnablePopoverProps): JSX.Element => {
  const styles = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const hasPendingPinRef = useRef(false);

  const handleOpenChange = (
    event: OpenPopoverEvents,
    data: OnOpenChangeData,
  ): void => {
    if (hasPendingPinRef.current) {
      hasPendingPinRef.current = false;
      setIsPinned(true);
      setIsOpen(true);
      return;
    }
    if (!data.open && isPinned && event.type === "mouseleave") {
      return;
    }
    setIsOpen(data.open);
    if (!data.open) {
      setIsPinned(false);
    }
  };

  const handleTriggerClick = (): void => {
    hasPendingPinRef.current = true;
  };

  const handleClose = (): void => {
    setIsPinned(false);
    setIsOpen(false);
  };

  return (
    <Popover
      openOnHover
      trapFocus={isPinned}
      withArrow
      positioning="above"
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <PopoverTrigger disableButtonEnhancement>
        {trigger({
          onClick: handleTriggerClick,
          "aria-haspopup": "dialog",
        })}
      </PopoverTrigger>
      <PopoverSurface
        className={mergeClasses(styles.popoverSurface, surfaceClassName)}
        role="dialog"
        aria-label={surfaceLabel}
      >
        {showCloseButton && (
          <button
            type="button"
            className={styles.popoverCloseButton}
            aria-label="Close"
            onClick={handleClose}
          >
            <Dismiss12Regular aria-hidden="true" />
          </button>
        )}
        {children}
      </PopoverSurface>
    </Popover>
  );
};
